import { belgium, checkVAT, france, germany, italy, luxembourg, netherlands } from '@accountable/jsvat';
import { electronicFormatIBAN, isSEPACountry, isValidIBAN } from 'ibantools';
import compact from 'lodash/compact';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import sum from 'lodash/sum';

import { isValidOGM } from './accounting/bankutils';
import { addCalendarDate, type CalendarDate, isAfter, isBefore, toCalendarDateNullable } from './dateutils';
import { roundAtSecondDecimal } from './numbers';

const VCS_LENGTH = 12;
const VCS_BODY_LENGTH = 10;
const EAN_18_LENGTH = 18;
const BE_GS1_PREFIX = '5414';
const MAX_PAYMENT_TERM_DAYS = 90;
const VAT_RATE_TOLERANCE = 0.6;
const AMOUNT_TOLERANCE = 0.011;
const DEFAULT_RAW_CONFIDENCE = 0.6;
const AUTO_VALIDATION_THRESHOLD = 0.9;
const BELGIAN_VAT_RATES = [0, 6, 12, 21];
const PERCENT = 100;

export const EU_VAT_COUNTRY_CODES = [
  'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'EL', 'ES', 'FI', 'FR', 'HR', 'HU', 'IE', 'IT', 'LT', 'LU', 'LV',
  'MT', 'NL', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK',
]; // prettier-ignore

export enum DocumentCheckFlag {
  ARITHMETIC_MISMATCH = 'ARITHMETIC_MISMATCH',
  FOREIGN_NON_EU = 'FOREIGN_NON_EU',
  FOREIGN_VAT_CHARGED = 'FOREIGN_VAT_CHARGED',
  IBAN_UNCONFIRMED = 'IBAN_UNCONFIRMED',
  MULTIPLE_SUPPLIER_IBANS_CHOOSE_SAFE = 'MULTIPLE_SUPPLIER_IBANS_CHOOSE_SAFE',
  NO_PROPERTY_KEY_MANUAL_ALLOCATION = 'NO_PROPERTY_KEY_MANUAL_ALLOCATION',
  READ_FROM_PHOTO = 'READ_FROM_PHOTO',
  RECIPIENT_VAT_IS_WORKSPACE = 'RECIPIENT_VAT_IS_WORKSPACE',
  REVERSE_CHARGE_NOT_STATED_VERIFY = 'REVERSE_CHARGE_NOT_STATED_VERIFY',
  REVERSE_CHARGE_WITH_VAT = 'REVERSE_CHARGE_WITH_VAT',
  SUPPLIER_VAT_INVALID = 'SUPPLIER_VAT_INVALID',
  SUPPLIER_VAT_IS_WORKSPACE_VAT = 'SUPPLIER_VAT_IS_WORKSPACE_VAT',
  SUPPLIER_VAT_MISSING = 'SUPPLIER_VAT_MISSING',
}

export enum DocumentVatRegime {
  EXEMPT = 'exempt',
  INTRA_EU = 'intraEU',
  NORMAL = 'normal',
  REVERSE_CHARGE = 'reverseCharge',
}

export enum VatNumberCheck {
  FORMAT_ONLY = 'FORMAT_ONLY',
  INVALID = 'INVALID',
  VALID = 'VALID',
}

/** The digits of the value, every other character dropped; empty for a nil value. */
export const toDigits = (value?: string | null): string => (value ?? '').replace(/\D/g, '');

const toCompactUppercase = (value?: string | null): string => (value ?? '').replace(/\s/g, '').toUpperCase();

export const isValidBeVat = (vat?: string | null): boolean => checkVAT(`BE${toDigits(vat)}`, [belgium]).isValid;

export const isValidIban = (iban?: string | null): boolean => isValidIBAN(electronicFormatIBAN(iban ?? '') ?? '');

export const isSepaIban = (iban?: string | null): boolean => isSEPACountry(toCompactUppercase(iban).slice(0, 2));

export const isValidForeignIban = (iban?: string | null, bic?: string | null): boolean =>
  isValidIban(iban) && (isSepaIban(iban) || !isEmpty(toCompactUppercase(bic)));

export const isValidVcs = (vcs?: string | null): boolean => {
  const digits = toDigits(vcs);
  return (
    digits.length === VCS_LENGTH && isValidOGM(`+++${digits.slice(0, 3)}/${digits.slice(3, 7)}/${digits.slice(7)}+++`)
  );
};

export const vcsEncodesInvoiceNumber = (vcs?: string | null, invoiceNumber?: string | null): boolean => {
  const invoiceDigits = toDigits(invoiceNumber);
  const vcsBody = toDigits(vcs).slice(0, VCS_BODY_LENGTH);
  const MIN_INVOICE_DIGITS = 4;
  const LONG_INVOICE_DIGITS = 6;
  if (invoiceDigits.length < MIN_INVOICE_DIGITS) {
    return false;
  }
  return (
    vcsBody.includes(invoiceDigits) ||
    invoiceDigits.includes(vcsBody.replace(/^0+/, '')) ||
    (invoiceDigits.length >= LONG_INVOICE_DIGITS && vcsBody.includes(invoiceDigits.slice(-LONG_INVOICE_DIGITS)))
  );
};

export const isValidEan18 = (ean?: string | null): boolean => {
  const digits = toDigits(ean);
  if (digits.length !== EAN_18_LENGTH) {
    return false;
  }
  const weighted = digits
    .slice(0, -1)
    .split('')
    .reverse()
    .map((digit, index) => Number(digit) * (index % 2 === 0 ? 3 : 1));
  const checkDigit = (10 - (sum(weighted) % 10)) % 10;
  return checkDigit === Number(digits[EAN_18_LENGTH - 1]);
};

export const hasBelgianGs1Prefix = (ean?: string | null): boolean => toDigits(ean).startsWith(BE_GS1_PREFIX);

const FRENCH_ALPHANUMERIC_KEY_PATTERN = /^(?=.*[A-Z])[0-9A-HJ-NP-Z]{2}\d{9}$/;

const FOREIGN_VAT_COUNTRIES = [france, germany, italy, luxembourg, netherlands];

const FOREIGN_VAT_FORMAT_ONLY_PATTERN = /^[A-Z0-9]{2,12}$/;
const COUNTRY_CODE_LENGTH = 2;

export const getVatCountryCode = (vat?: string | null): string | null => {
  const normalized = toCompactUppercase(vat);
  return /^[A-Z]{2}/.test(normalized) ? normalized.slice(0, COUNTRY_CODE_LENGTH) : null;
};

export const isEuVatCountry = (countryCode: string): boolean => EU_VAT_COUNTRY_CODES.includes(countryCode);

export const checkForeignVat = (vat?: string | null): VatNumberCheck => {
  const countryCode = getVatCountryCode(vat);
  if (isNil(countryCode) || countryCode === 'BE') {
    return VatNumberCheck.INVALID;
  }
  const body = toCompactUppercase(vat).slice(COUNTRY_CODE_LENGTH);
  if (countryCode === 'FR' && FRENCH_ALPHANUMERIC_KEY_PATTERN.test(body)) {
    return VatNumberCheck.FORMAT_ONLY;
  }
  const { country, isValid } = checkVAT(toCompactUppercase(vat), FOREIGN_VAT_COUNTRIES);
  if (!isNil(country)) {
    return isValid ? VatNumberCheck.VALID : VatNumberCheck.INVALID;
  }
  return FOREIGN_VAT_FORMAT_ONLY_PATTERN.test(body) ? VatNumberCheck.FORMAT_ONLY : VatNumberCheck.INVALID;
};

export interface ViesLookupResult {
  exists: boolean;
  name?: string | null;
}

export type ViesLookup = (vat: string) => Promise<ViesLookupResult | null>;

export const isWorkspaceVat = (vat: string | null | undefined, workspaceVat: string | null | undefined): boolean =>
  !isEmpty(toDigits(vat)) &&
  toDigits(vat) === toDigits(workspaceVat) &&
  getVatCountryCode(vat) === getVatCountryCode(workspaceVat);

export const isInferredRatePlausible = (net: number, vat: number, printedRate: number): boolean =>
  net !== 0 &&
  Math.abs(roundAtSecondDecimal((Math.abs(vat) / Math.abs(net)) * PERCENT) - printedRate) <= VAT_RATE_TOLERANCE;

export const isStandardBelgianRate = (net: number, vat: number): boolean => {
  if (net === 0) {
    return false;
  }
  const inferred = (Math.abs(vat) / Math.abs(net)) * PERCENT;
  return BELGIAN_VAT_RATES.some((rate) => Math.abs(inferred - rate) <= VAT_RATE_TOLERANCE);
};

export const isNetPlusVatEqualGross = (net: number, vat: number, gross: number): boolean =>
  Math.abs(Math.abs(net) + Math.abs(vat) - Math.abs(gross)) <= AMOUNT_TOLERANCE;

export interface DueDateCheck {
  dueAfterIssue: boolean;
  withinPaymentTerm: boolean;
}

const toReadableCalendarDate = (value?: string | null): CalendarDate | null => {
  try {
    return toCalendarDateNullable(value);
  } catch {
    return null;
  }
};

export const checkDueDate = (issueDate?: string | null, dueDate?: string | null): DueDateCheck | null => {
  const issue = toReadableCalendarDate(issueDate);
  const due = toReadableCalendarDate(dueDate);
  if (isNil(issue) || isNil(due)) {
    return null;
  }
  return {
    dueAfterIssue: !isBefore(due, issue),
    withinPaymentTerm: !isAfter(due, addCalendarDate(issue, MAX_PAYMENT_TERM_DAYS, 'days')),
  };
};

export const isSettlementConsistent = (
  periodTotal: number,
  advances: number,
  billed: number,
  tolerance = AMOUNT_TOLERANCE
): boolean => Math.abs(periodTotal - advances - billed) <= tolerance;

export interface FundCallLine {
  amount: number;
  quotity: number;
  denominator: number;
}

export const isFundCallConsistent = (lines: FundCallLine[], total: number, tolerance: number): boolean =>
  Math.abs(sum(lines.map((line) => (line.amount * line.quotity) / line.denominator)) - total) <= tolerance;

export const isAccountPositionConsistent = (
  opening: number,
  calls: number,
  payments: number,
  closing: number,
  tolerance = AMOUNT_TOLERANCE
): boolean => Math.abs(opening + calls - payments - closing) <= tolerance;

export interface WorkspaceIdentity {
  vat?: string | null;
  ibans?: string[];
  addresses?: string[];
  managerFirstNames?: string[];
}

export const isWorkspaceIdentityValue = (value: string | null | undefined, workspace: WorkspaceIdentity): boolean => {
  const normalized = toCompactUppercase(value);
  if (isEmpty(normalized)) {
    return false;
  }
  const knownValues = compact([
    toCompactUppercase(workspace.vat),
    ...(workspace.ibans ?? []).map(toCompactUppercase),
    ...(workspace.addresses ?? []).map(toCompactUppercase),
    ...(workspace.managerFirstNames ?? []).map(toCompactUppercase),
  ]);
  return knownValues.includes(normalized);
};

export interface VatRegimeInput {
  supplierVat?: string | null;
  vatTotal?: number | null;
  reverseCharge?: boolean;
  exempt?: boolean;
}

export interface VatRegimeResult {
  regime: DocumentVatRegime;
  flags: DocumentCheckFlag[];
}

export const inferVatRegime = ({ supplierVat, vatTotal, reverseCharge, exempt }: VatRegimeInput): VatRegimeResult => {
  const countryCode = getVatCountryCode(supplierVat) ?? 'BE';
  const vatCharged = Math.abs(vatTotal ?? 0) > 0;
  if (countryCode !== 'BE') {
    if (!isEuVatCountry(countryCode)) {
      return {
        regime: vatCharged ? DocumentVatRegime.NORMAL : DocumentVatRegime.EXEMPT,
        flags: [DocumentCheckFlag.FOREIGN_NON_EU],
      };
    }
    return vatCharged
      ? { regime: DocumentVatRegime.NORMAL, flags: [DocumentCheckFlag.FOREIGN_VAT_CHARGED] }
      : { regime: DocumentVatRegime.INTRA_EU, flags: [] };
  }
  if (reverseCharge === true) {
    return { regime: DocumentVatRegime.REVERSE_CHARGE, flags: [] };
  }
  if (exempt === true || !vatCharged) {
    return { regime: DocumentVatRegime.EXEMPT, flags: [] };
  }
  return { regime: DocumentVatRegime.NORMAL, flags: [] };
};

export interface ScoreBonus {
  passed: boolean | null;
  weight: number;
  reason: string;
}

export interface FieldScore {
  score: number;
  reasons: string[];
}

export const scoreField = (rawConfidence: number, bonuses: ScoreBonus[]): FieldScore => {
  const applicable = bonuses.filter((bonus) => !isNil(bonus.passed));
  const total = applicable.reduce(
    (acc, bonus) => acc + (bonus.passed === true ? bonus.weight : -bonus.weight),
    rawConfidence
  );
  return {
    score: roundAtSecondDecimal(Math.max(0, Math.min(1, total))),
    reasons: applicable.map((bonus) => `${bonus.passed === true ? '+' : '-'}${bonus.reason}`),
  };
};

export interface IbanObservation {
  documents: number;
  recipients: number;
}

export interface DocumentChecksContext {
  workspace: WorkspaceIdentity;
  ibanObservations?: Record<string, IbanObservation>;
  registryNamesByVat?: Record<string, string[]>;
}

export interface DocumentChecksInput {
  documentKind: string;
  posting: boolean;
  isPhoto?: boolean;
  supplier: { name?: string | null; vat?: string | null; ibans?: string[]; bic?: string | null };
  document: {
    invoiceNumber?: string | null;
    /** Number the document refers to (reminder, credit note), read for the VCS link only. */
    referencedInvoiceNumber?: string | null;
    issueDate?: string | null;
    dueDate?: string | null;
  };
  amounts: {
    netTotal?: number | null;
    vatTotal?: number | null;
    grossTotal?: number | null;
    vatRates?: number[];
    reverseCharge?: boolean;
    reverseChargeMention?: string | null;
  };
  payment: { iban?: string | null; bic?: string | null; structuredCommunication?: string | null };
  matchingKeys: {
    eans?: string[];
    hasSupplyAddress?: boolean;
    hasPropertyKey: boolean;
    recipientVat?: string | null;
  };
  rawConfidence?: Record<string, number>;
}

export interface DocumentChecksResult {
  fields: Record<string, FieldScore>;
  checks: Record<string, boolean | number | string>;
  flags: string[];
  overall: number;
  regime: DocumentVatRegime;
  autoValidationCandidate: boolean;
}

const INVOICE_LIKE_KINDS = ['SUPPLIER_INVOICE', 'CREDIT_NOTE', 'INTERIM_UTILITY_INVOICE', 'UTILITY_SETTLEMENT'];

const fieldConfidence = (
  confidences: Record<string, number> | undefined,
  key: string,
  fallback = DEFAULT_RAW_CONFIDENCE
): number => {
  const matchingKey = Object.keys(confidences ?? {}).find(
    (candidate) => candidate === key || candidate.startsWith(key)
  );
  return isNil(matchingKey) ? fallback : (confidences?.[matchingKey] ?? fallback);
};

const normalizeIban = (iban: string): string => iban.replace(/\s/g, '');

export const runDocumentChecks = (input: DocumentChecksInput, context: DocumentChecksContext): DocumentChecksResult => {
  const { supplier, document, amounts, payment, matchingKeys, rawConfidence } = input;
  const fields: Record<string, FieldScore> = {};
  const checks: Record<string, boolean | number | string> = {};
  const flags: string[] = [];
  const regimeResult = inferVatRegime({
    supplierVat: supplier.vat,
    vatTotal: amounts.vatTotal,
    reverseCharge: amounts.reverseCharge,
  });
  flags.push(...regimeResult.flags);
  if (input.isPhoto === true) {
    flags.push(DocumentCheckFlag.READ_FROM_PHOTO);
  }

  if (isEmpty(supplier.vat)) {
    fields['supplier.vat'] = { score: 0, reasons: ['absent'] };
    if (INVOICE_LIKE_KINDS.includes(input.documentKind)) {
      flags.push(DocumentCheckFlag.SUPPLIER_VAT_MISSING);
    }
  } else {
    const isBelgian = (getVatCountryCode(supplier.vat) ?? 'BE') === 'BE';
    const vatValid = isBelgian ? isValidBeVat(supplier.vat) : checkForeignVat(supplier.vat) !== VatNumberCheck.INVALID;
    checks.vat_valid = vatValid;
    if (!vatValid) {
      flags.push(DocumentCheckFlag.SUPPLIER_VAT_INVALID);
    }
    const registryNames = context.registryNamesByVat?.[toCompactUppercase(supplier.vat)];
    const supplierName = (supplier.name ?? '').toLowerCase();
    const nameMatch = isNil(registryNames)
      ? null
      : registryNames.some(
          (name) => supplierName.includes(name.toLowerCase()) || name.toLowerCase().includes(supplierName)
        );
    const isWorkspace = isWorkspaceVat(supplier.vat, context.workspace.vat);
    if (isWorkspace) {
      flags.push(DocumentCheckFlag.SUPPLIER_VAT_IS_WORKSPACE_VAT);
    }
    fields['supplier.vat'] = scoreField(fieldConfidence(rawConfidence, 'supplier.vat'), [
      { passed: vatValid, weight: 0.15, reason: 'valid vat' },
      { passed: nameMatch, weight: 0.15, reason: 'registry name×vat' },
      { passed: isWorkspace ? false : null, weight: 0.5, reason: 'workspace VAT used as supplier' },
    ]);
  }

  const ibans = [...new Set(compact([...(supplier.ibans ?? []), payment.iban]).map(normalizeIban))];
  if (!isEmpty(ibans)) {
    const knownBic = [payment.bic, supplier.bic].find((bic) => !isEmpty(bic));
    const ibansValid = ibans.every((iban) => isValidForeignIban(iban, knownBic));
    checks.iban_valid = ibansValid;
    if (!ibansValid) {
      flags.push(DocumentCheckFlag.IBAN_UNCONFIRMED);
    }
    const observations = ibans.map((iban) => context.ibanObservations?.[iban] ?? { documents: 0, recipients: 0 });
    const documentsCount = Math.max(...observations.map((observation) => observation.documents));
    const recipientsCount = Math.max(...observations.map((observation) => observation.recipients));
    const hasMultipleIbans = ibans.length > 1;
    if (hasMultipleIbans) {
      flags.push(DocumentCheckFlag.MULTIPLE_SUPPLIER_IBANS_CHOOSE_SAFE);
    }
    fields['payment.iban'] = scoreField(
      fieldConfidence(rawConfidence, 'payment.iban', fieldConfidence(rawConfidence, 'supplier.iban')),
      [
        { passed: ibansValid, weight: 0.15, reason: 'iban mod97' },
        { passed: documentsCount >= 2, weight: 0.1, reason: `seen on ${documentsCount} docs` },
        {
          passed: recipientsCount >= 2,
          weight: 0.15,
          reason: `seen for ${recipientsCount} distinct recipients (cross-account)`,
        },
        { passed: hasMultipleIbans ? false : null, weight: 0.3, reason: 'two IBANs, none designated' },
      ]
    );
  }

  const invoiceNumber = document.invoiceNumber;
  if (!isEmpty(payment.structuredCommunication) && !isNil(payment.structuredCommunication)) {
    const vcsValid = isValidVcs(payment.structuredCommunication);
    const linksInvoice = [invoiceNumber, document.referencedInvoiceNumber].some((candidate) =>
      vcsEncodesInvoiceNumber(payment.structuredCommunication, candidate)
    );
    checks.vcs_valid = vcsValid;
    checks.vcs_links_invoice_number = linksInvoice;
    fields['payment.structured_communication'] = scoreField(
      fieldConfidence(rawConfidence, 'payment.structured_communication'),
      [{ passed: vcsValid, weight: 0.2, reason: 'mod97' }]
    );
    if (!isEmpty(invoiceNumber)) {
      fields['document.invoice_number'] = scoreField(fieldConfidence(rawConfidence, 'document.invoice_number'), [
        { passed: linksInvoice ? true : null, weight: 0.2, reason: 'embedded in VCS' },
      ]);
    }
  } else if (!isEmpty(invoiceNumber)) {
    fields['document.invoice_number'] = scoreField(fieldConfidence(rawConfidence, 'document.invoice_number'), []);
  }

  const isMandatoryChecked = input.posting && INVOICE_LIKE_KINDS.includes(input.documentKind);
  if (isMandatoryChecked && isEmpty(invoiceNumber)) {
    fields['document.invoice_number'] = { score: 0, reasons: ['absent'] };
  }

  const eans = matchingKeys.eans ?? [];
  if (!isEmpty(eans)) {
    const eansValid = eans.every(isValidEan18);
    checks.ean_checksum = eansValid;
    fields['matching_keys.ean'] = scoreField(fieldConfidence(rawConfidence, 'matching_keys.ean'), [
      { passed: eansValid, weight: 0.15, reason: 'GS1 check digit' },
      {
        passed: matchingKeys.hasSupplyAddress ?? matchingKeys.hasPropertyKey,
        weight: 0.1,
        reason: 'supply address present',
      },
      { passed: eans.every(hasBelgianGs1Prefix), weight: 0.05, reason: 'BE prefix 5414' },
    ]);
  }

  const { netTotal, vatTotal, grossTotal } = amounts;
  if (!isNil(netTotal) && !isNil(vatTotal) && !isNil(grossTotal)) {
    const arithmeticOk = isNetPlusVatEqualGross(netTotal, vatTotal, grossTotal);
    const inferred = netTotal === 0 ? null : roundAtSecondDecimal((Math.abs(vatTotal) / Math.abs(netTotal)) * PERCENT);
    checks.net_plus_vat_eq_gross = arithmeticOk;
    if (!isNil(inferred)) {
      checks.vat_rate_inferred_pct = inferred;
    }
    if (!arithmeticOk) {
      flags.push(DocumentCheckFlag.ARITHMETIC_MISMATCH);
    }
    const printedRates = amounts.vatRates ?? [];
    const rateOk = printedRates.length === 1 ? isInferredRatePlausible(netTotal, vatTotal, printedRates[0]) : null;
    fields['amounts.gross_total'] = scoreField(fieldConfidence(rawConfidence, 'amounts.gross_total'), [
      { passed: arithmeticOk, weight: 0.2, reason: 'net+vat=gross' },
      { passed: rateOk, weight: 0.1, reason: `rate ${inferred}% plausible` },
    ]);
  } else if (!isNil(grossTotal)) {
    fields['amounts.gross_total'] = scoreField(
      fieldConfidence(rawConfidence, 'amounts.gross_total', fieldConfidence(rawConfidence, 'amounts.amount_due')),
      []
    );
  }
  if (isMandatoryChecked && isNil(grossTotal)) {
    fields['amounts.gross_total'] = { score: 0, reasons: ['absent'] };
  }
  if (amounts.reverseCharge === true && (vatTotal ?? 0) !== 0) {
    flags.push(DocumentCheckFlag.REVERSE_CHARGE_WITH_VAT);
  }
  if (amounts.reverseCharge === true && isEmpty(amounts.reverseChargeMention)) {
    flags.push(DocumentCheckFlag.REVERSE_CHARGE_NOT_STATED_VERIFY);
  }

  const dueDateCheck = checkDueDate(document.issueDate, document.dueDate);
  if (!isNil(dueDateCheck)) {
    checks.due_after_invoice = dueDateCheck.dueAfterIssue;
    fields['document.due_date'] = scoreField(fieldConfidence(rawConfidence, 'document.due_date'), [
      { passed: dueDateCheck.dueAfterIssue, weight: 0.1, reason: 'due ≥ invoice' },
      { passed: dueDateCheck.dueAfterIssue ? dueDateCheck.withinPaymentTerm : null, weight: 0.05, reason: '≤ 90 days' },
    ]);
  }

  if (isWorkspaceVat(matchingKeys.recipientVat, context.workspace.vat)) {
    flags.push(DocumentCheckFlag.RECIPIENT_VAT_IS_WORKSPACE);
  }
  if (!matchingKeys.hasPropertyKey && isEmpty(eans)) {
    flags.push(DocumentCheckFlag.NO_PROPERTY_KEY_MANUAL_ALLOCATION);
  }

  const scores = Object.values(fields).map((field) => field.score);
  const overall = isEmpty(scores) ? 0 : roundAtSecondDecimal(sum(scores) / scores.length);
  const blockingFlags = flags.filter((flag) => flag !== DocumentCheckFlag.NO_PROPERTY_KEY_MANUAL_ALLOCATION);
  return {
    fields,
    checks,
    flags,
    overall,
    regime: regimeResult.regime,
    autoValidationCandidate:
      overall >= AUTO_VALIDATION_THRESHOLD &&
      isEmpty(blockingFlags) &&
      input.posting &&
      checks.due_after_invoice !== false,
  };
};
